import { useEffect, useRef } from "react";
import i18n, { DEFAULT_LANGUAGE, detectInitialLanguage, isSupportedLanguage, changeLanguage } from "src/i18n";
import type { LanguageCode } from "src/i18n";
import { loadLanguage } from "src/utils/languageStorage";
import { useEnabledLanguagesContext } from "src/components/providers/EnabledLanguagesProvider";

/**
 * Keeps the active UI language consistent with the set of languages currently
 * released (base languages + feature-flag-enabled ones). Renders nothing.
 *
 * The synchronous startup detection in `src/i18n` can only see the base
 * languages (feature flags resolve asynchronously). Once flags load, this gate:
 *   - restores an explicitly stored choice if it is now allowed,
 *   - falls back to English if a stored choice is not currently released
 *     (without erasing the stored value, so it returns if the flag is re-enabled),
 *   - otherwise auto-detects among the enabled languages when there is no
 *     stored preference.
 *
 * KNOWN LIMITATION (intentional): a user whose stored language is a
 * feature-flagged (non-base) one gets the base-language fallback on first paint,
 * then this gate switches to their language once flags resolve — a brief flash.
 * Eliminating it would require async init or SSR; the current-language clamping
 * here is deliberate, so don't "fix" it by removing the clamp.
 */
const LanguageGate = (): null => {
  // Reference-stable across renders (see EnabledLanguagesProvider), so it is a
  // safe effect dependency.
  const enabledLanguages = useEnabledLanguagesContext();
  // Browser auto-detection runs at most once per session. Without this guard,
  // a user with no stored preference whose browser language is flag-gated would
  // see the UI silently switch to that language the moment its flag resolves.
  const hasAutoDetected = useRef(false);

  useEffect(() => {
    const enabledCodes = enabledLanguages.map((l) => l.code);
    const stored = loadLanguage();
    let target: LanguageCode;
    if (stored) {
      // A stored value signals explicit user intent, so never auto-detect over
      // it — even an unsupported/unreleased code falls back to the default
      // rather than the browser language.
      target = isSupportedLanguage(stored) && enabledCodes.includes(stored) ? stored : DEFAULT_LANGUAGE;
    } else if (!hasAutoDetected.current) {
      hasAutoDetected.current = true;
      target = detectInitialLanguage(enabledCodes);
    } else {
      // Flags resolved but the user has no preference and we already auto-detected;
      // don't yank the language out from under them on a later flag change.
      return;
    }
    if (i18n.language !== target) {
      // persist: false — clamping must not overwrite the user's stored preference.
      void changeLanguage(target, { persist: false });
    }
  }, [enabledLanguages]);

  return null;
};

export default LanguageGate;
